<template>
  <div v-if="!data.component || (data.component && !data.component.length && !component.length)">
    <v-progress-linear
      indeterminate
      color="#26384b"
      rounded
      height="4"
    />
  </div>
  <div v-else>
    <p class="titleModal">
      {{ $t('analysisRequests.labels.summary') }}
    </p>
    <VTextField
      v-if="data.name && data.name.length"
      v-model="data.name"
      outlined
      :label="$t('forms.labels.name')"
      readonly
    />
    <VAutocomplete
      v-model="data.client"
      :label="$t('forms.labels.client')"
      :items="mutableValue.clients"
      :rules="{ required: false }"
      outlined
      data-cy="client-Summary-AnalysisRequest"
      readonly
    />
    <VSelect
      v-model="data.site"
      :required="false"
      :label="$t('forms.labels.site')"
      :items="mutableValue.sites"
      outlined
      readonly
    />
    <SelectWithTreeview
      v-model="data.component"
      :label="$t('forms.labels.component')"
      :items="component"
      :required="false"
      :isOutlined="true"
      readonly
    />
    <VSelect
      v-model="data.type"
      :required="false"
      :label="$t('forms.labels.sampleType')"
      :items="types"
      itemValue="key"
      outlined
      data-cy="type-Summary-AnalysisRequest"
      readonly
    />
    <v-row class="pt-0">
      <v-col
        md="3"
        sm="3"
        cols="12"
      >
        <VTextField
          v-model="data.poNumber"
          :label="$t('forms.labels.poNumber')"
          outlined
          @input="updateData"
        />
      </v-col>
    </v-row>
    <div
      v-for="(sample, index) in data.samples"
      :key="sample.id"
    >
      <h1 class="pb-4">
        {{ $t('forms.labels.sample') }} {{ index + 1 }}
      </h1>
      <v-row
        align="center"
        justify="center"
      >
        <v-col class="py-0">
          <VTextField
            v-model="sample.identification"
            :label="$t('forms.labels.identification')"
            outlined
            readonly
          />
        </v-col>

        <v-col
          v-if="isSampleType(sampleTypes.COUPON)"
          class="py-0"
        >
          <VDatePicker
            :value="sample.installedDate"
            :label="$t('analysisRequests.labels.installedDate')"
            :rules="{ required: false }"
            outlined
            viewonly
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pt-0">
          <VDateTimePicker
            :value="sample.collectedDate"
            :label="$t('analysisRequests.labels.collectedDate')"
            :rules="{ required: false }"
            :locale="datePickerLocales[$store.getters.lang]"
            outlined
            viewonly
          />
        </v-col>
        <v-col
          v-if="isSampleType(sampleTypes.COUPON)"
          class="py-0"
        >
          <VTextField
            :value="daysInstalled(sample.installedDate, sample.collectedDate)"
            :label="$t('analysisRequests.labels.daysInstalled')"
            outlined
            readonly
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-if="isSampleType(sampleTypes.COUPON)"
          class="py-0"
        >
          <VAutocomplete
            v-model="sample.coupon"
            :label="$t('analysisRequests.labels.coupon')"
            :items="mutableValue.coupons"
            outlined
            readonly
          />
        </v-col>
        <v-col
          v-if="isSampleType(sampleTypes.COUPON)"
          class="py-0"
        >
          <VTextField
            v-model="sample.code"
            :label="$t('analysisRequests.labels.couponCode')"
            outlined
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pb-0">
          <VTextField
            v-model="sample.comment"
            :label="$t('forms.labels.note')"
            :rules="{ required: false }"
            outlined
            data-cy="commentDescriptionSummaryAR"
            readonly
          />
        </v-col>
      </v-row>
    </div>
    <p class="titleModal">
      {{ $t('forms.labels.treatment') }}
    </p>
    <div v-if="isSampleType(sampleTypes.WATER)">
      <VTextField
        v-model="data.treatment"
        :label="$t('forms.labels.treatment')"
        :rules="{ required: false }"
        outlined
        data-cy="treatmentSummaryAR"

        readonly
      />
      <VTextField
        v-model="data.otherTreatment"
        :label="$t('forms.labels.otherTreatment')"
        :rules="{ required: false }"
        outlined
        data-cy="otherTreatmentSummaryAR"
        readonly
      />
    </div>
    <div v-if="isSampleType(sampleTypes.GLYCOL)">
      <VSelect
        v-model="data.glycolType"
        :label="$t('forms.labels.type')"
        :items="glycolTypes"
        readonly
        itemValue="key"
        itemText="name"
        outlined
        data-cy="selectSite"
      />
      <VTextField
        v-if="data.glycolType === 'other'"
        v-model="data.glycolOther"
        outlined
        readonly
        :label="$t('forms.labels.moreInfo')"
        :rules="{ min: 1, max: 200 }"
      />
      <VTextField
        v-model="data.treatment"
        outlined
        readonly
        :label="$t('forms.labels.treatment')"
        :rules="{ required: true, min: 1, max: 200 }"
      />
      <VTextField
        v-model="data.otherTreatment"
        outlined
        readonly
        :label="$t('forms.labels.otherTreatment')"
        :rules="{ max: 1000 }"
      />
    </div>
    <div v-if="isSampleType(sampleTypes.LEGIONELLOSIS) || isSampleType(sampleTypes.LEGIONELLA_CULTRURE)">
      <VTextField
        v-model="data.treatment"
        :label="$t('forms.labels.treatment')"
        outlined
        readonly
      />
      <VTextField
        v-model="data.biocideInfo"
        :label="$t('forms.labels.biocideInfo')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <v-row>
        <v-col>
          <VTextField
            v-model="data.temperature"
            :label="$t('forms.labels.temperature')"
            outlined
            readonly
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="data.temperatureUnit"
            :label="$t('forms.labels.units')"
            :items="tempratureUnits"
            itemValue="key"
            outlined
          />
        </v-col>
      </v-row>
    </div>
    <div v-if="isSampleType(sampleTypes.DEPOSIT)">
      <VTextField
        v-model="data.treatment"
        :label="$t('forms.labels.treatment')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.otherTreatment"
        :label="$t('forms.labels.otherTreatment')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.estimatedDepositAge"
        :label="$t('forms.labels.estimatedDepositAge')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.application"
        :label="$t('forms.labels.application')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VSelect
        v-model="data.materialType"
        :label="$t('forms.labels.materialType')"
        itemValue="key"
        :items="materialTypes"
        :required="false"
        outlined
        readonly
      />
      <VSelect
        v-model="data.waterType"
        :label="$t('forms.labels.waterType')"
        itemValue="key"
        :items="waterTypes"
        :required="false"
        outlined
        readonly
      />
      <VTextarea
        v-model="data.incidents"
        :label="$t('forms.labels.incidents')"
        :rules="{ required: false, max: 1000 }"
        outlined
        readonly
      />
    </div>
    <div v-if="isSampleType(sampleTypes.CONTENANCE)">
      <VDatePicker
        :value="data.tracerAddedDate"
        :label="$t('analysisRequests.contenance.tracerAddedDate')"
        :rules="{ required: false }"
        outlined
        viewonly
      />
      <VDatePicker
        :value="data.collectionLossDate"
        :label="$t('analysisRequests.contenance.collectionLossDate')"
        :rules="{ required: false }"
        outlined
        viewonly
      />
      <VTextField
        v-model="data.quantityAdded"
        :label="$t('analysisRequests.contenance.quantityAdded')"
        suffix="g"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.treatment"
        :label="$t('analysisRequests.contenance.treatment')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.otherTreatment"
        :label="$t('analysisRequests.contenance.otherTreatment')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.estimatedVolume"
        :label="$t('analysisRequests.contenance.estimatedVolume')"
        suffix="l"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.addedLithium"
        :label="$t('analysisRequests.contenance.addedLithium')"
        suffix="kg"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.addedTracer"
        :label="$t('analysisRequests.contenance.addedTracer')"
        suffif="l"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VSelect
        v-model="data.evaluation"
        :label="$t('analysisRequests.contenance.evaluation')"
        itemValue="key"
        :items="evaluationTypes"
        :required="false"
        outlined
        readonly
      />
    </div>
    <div v-if="isSampleType(sampleTypes.RESIN)">
      <VTextField
        v-model="data.treatment"
        :label="$t('forms.labels.treatment')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.makerName"
        :label="$t('forms.labels.makerName')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.resinNumber"
        :label="$t('forms.labels.resinNumber')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VSelect
        v-model="data.resinType"
        :label="$t('forms.labels.resinType')"
        itemValue="key"
        :items="resinTypes"
        :required="false"
        outlined
        readonly
      />
      <VTextField
        v-model="data.age"
        :label="$t('forms.labels.age')"
        :rules="{ required: false }"
        outlined
        readonly
      />
      <VTextField
        v-model="data.analysisReason"
        :label="$t('forms.labels.analysisReason')"
        :rules="{ required: false }"
        outlined
        readonly
      />
    </div>
    <div v-if="isSampleType(sampleTypes.COUPON)">
      <VTextField
        v-model="data.treatment"
        :label="$t('forms.labels.treatment')"
        outlined
        readonly
      />
      <VAutocomplete
        v-model="data.applicableNorms"
        :label="$t('analysisRequests.labels.applicableNorms')"
        :items="standardFamilies"
        outlined
        readonly
      />
    </div>
    <div v-if="isSampleType(sampleTypes.OTHER)">
      <VTextarea
        v-model="data.AROtherAdditionalInfo"
        :label="$t('forms.labels.info')"
        :rules="{ required: false, max: 10000 }"
        outlined
        readonly
      />
    </div>
    <div>
      <VTextarea
        v-model="data.comment"
        :label="$t('forms.labels.note')"
        :rules="{ required: false, max: 1000 }"
        outlined
        readonly
      />
    </div>
    <div v-if="!isSampleType(sampleTypes.DEPOSIT)">
      <VAutocomplete
        v-model="data.analysisTypes"
        :label="$t('samples.labels.analysisTypes')"
        :items="displayedAnalysisTypes"
        outlined
        itemValue="id"
        multiple
        data-cy="analysisTypes-AnalysisRequest"
        readonly
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import SelectWithTreeview from '@/components/shared/widgets/forms/SelectWithTreeview';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete';
import VDatePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDatePicker';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea';
import VDateTimePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDateTimePicker';
import sampleTypes from '@/constants/sampleTypes';
import resinTypes from '@/constants/resinTypes';
import waterTypes from '@/constants/waterTypes';
import materialTypes from '@/constants/materialTypes';
import evaluationTypes from '@/constants/evaluationTypes';
import _datePickerLocales from '@/locales/date-picker-locales';
import units from '@/constants/tempratureUnits';

export default {
    components: {
        VDateTimePicker,
        SelectWithTreeview,
        VAutocomplete,
        VSelect,
        VTextarea,
        VTextField,
        VDatePicker,
    },
    props: {
        mutableValue: {
            type: Object,
            required: true,
        },
        isWorkOrder: {
            type: Boolean,
            default: false,
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            standardFamilies: [],
            data: {},
            component: [{}],
            displayedAnalysisTypes: [],
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
            'getSample',
            'aRAnalysisTypes',
        ]),
        sampleTypes() {
            return sampleTypes;
        },
        daysInstalled() {
            return (installDate, dateAndTime) => moment(dateAndTime).diff(moment(installDate), 'days');
        },
        isSampleType() {
            return type => this.data.type === type;
        },
        evaluationTypes() {
            return evaluationTypes.map(type => ({
                name: this.$t(`evaluationTypes.${type}`),
                key: type,
            }));
        },
        materialTypes() {
            return materialTypes.map(type => ({
                name: this.$t(`materialTypes.${type}`),
                key: type,
            }));
        },
        waterTypes() {
            return waterTypes.map(type => ({
                name: this.$t(`waterTypes.${type}`),
                key: type,
            }));
        },
        resinTypes() {
            return resinTypes.map(type => ({
                name: this.$t(`resinTypes.${type}`),
                key: type,
            }));
        },
        types() {
            return Object.values(sampleTypes).map(type => ({
                name: this.$t(`sampleTypes.${type.charAt(0).toLowerCase()}${type.substring(1)}`),
                key: type,
            }));
        },
        datePickerLocales() {
            return _datePickerLocales;
        },
        tempratureUnits() {
            return Object.entries(units).map(([key, value]) => ({
                key,
                name: value,
            }));
        },
    },
    watch: {
        getSample: {
            handler() {
                this.data = cloneDeep(this.getSample);
            },
            deep: true,
        },
    },
    async created() {
        await this.getApplicableNorms();
        await this.getGlycolTypes();

        if (Object.keys(this.getSample).length === 0) {
            // Redirect from wo AR
            // store does not exist
            this.data = this.initDataFromProps();
        } else {
            this.data = cloneDeep(this.getSample);
        }
        await this.setComponent();
        await this.setAnalysisTypes();
    },
    async mounted() {
        await this.setComponent();
    },
    methods: {
        async getApplicableNorms() {
            const { data } = await this.$rq.getStandardFamilies();
            this.standardFamilies = data.standardFamilies;
        },
        async getGlycolTypes() {
            const { data } = await this.$rq.getGlycolTypes();
            this.glycolTypes = data.types;
        },
        initDataFromProps() {
            return {
                ...this.mutableValue.analysisRequest,
                client: this.mutableValue.analysisRequest.client.id,
                site: this.mutableValue.analysisRequest.site.id,
                type: this.mutableValue.analysisRequest.type,
                component: this.mutableValue.analysisRequest.component.id,
                // DESCRIPTION
                samples: this.mutableValue.analysisRequest.samples,
                note: this.mutableValue.analysisRequest.note,
                // Legionellosis
                biocideInfo: this.mutableValue.analysisRequest.biocideInfo,
                sampleDate: null,
                temperature: this.mutableValue.analysisRequest.temperature,
                sampledBy: this.mutableValue.analysisRequest.sampledBy,
                coupons: this.mutableValue.coupons,
                analysisTypes: this.mutableValue.analysisRequest.analysisTypes,
            };
        },
        async setComponent() {
            if (this?.mutableValue?.analysisRequest?.component) {
                // Redirect from wo
                this.component = [this.mutableValue.analysisRequest.component];
            } else if (this.data?.component) {
                const { data } = await this.$rq.getNode(this.data.component, 'component');
                if (data) {
                    this.component = [cloneDeep(data)];
                }
            }
        },
        setAnalysisTypes() {
            // Redirect from lab wo
            if (this?.mutableValue?.analysisRequest) {
                this.displayedAnalysisTypes = this.mutableValue.analysisRequest.analysisTypes;
                this.data.analysisTypes = this.mutableValue.analysisRequest.analysisTypes.map(({ id }) => id);
                return;
            }
            // Redirect from create new AR
            if (this.data.analysisTypes.length) {
                const allAnalysisTypes = [...this.mutableValue.analysisTypes, ...this.mutableValue.analysisTypesByLegionella];
                const validAnalysisTypes = [];
                this.data.analysisTypes.forEach(analysisTypeKey => {
                    validAnalysisTypes.push(allAnalysisTypes.find(anType => anType.key === analysisTypeKey));
                });
                this.displayedAnalysisTypes = validAnalysisTypes.filter(at => at)
                    .map(({ id, name }) => ({ id, name: name[this.userLang] || name[this.teamMainLang] }));
                this.data.analysisTypes = validAnalysisTypes.filter(at => at).map(({ id }) => id);
            }
        },
        updateData() {
            this.$emit('sendData', this.data);
        },
    },
};
</script>
